"use client";

import Image from "next/image";
import { ChevronRight, Check } from "lucide-react";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import { AnalysisPlayback } from "../_components/analysis-playback";
import { WebsiteCtaLink } from "@/app/(website)/_components/website-cta-link";
import { ReportFeatureShowcase } from "@/app/(website)/_components/report-feature-showcase";
import { DataSourcesSection } from "@/app/(website)/_components/data-sources-section";

export default function PlatformPage() {
  return (
    <div className="relative bg-slate-950">
      {/* Hero / Store Owners’ Pain Section */}
      <section className="pt-20 pb-16 md:pt-32 md:pb-24 bg-slate-950">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <ScrollReveal>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-8">
                Ecommerce stores are drowning in data, but still flying blind
              </h1>
            </ScrollReveal>
            <ScrollReveal delay={100}>
              <p className="text-xl text-purple-400 mb-12 font-medium max-w-2xl mx-auto">
                Storita connects your store data and surfaces the signals that matter - in real time.
              </p>
            </ScrollReveal>
          </div>

          {/* Bullet Section */}
          <div className="max-w-3xl mx-auto mt-20 mb-32">
            <ScrollReveal delay={300}>
              <div className="text-center mb-10">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-600">
                  Signals Storita continuously monitors
                </span>
              </div>
            </ScrollReveal>

            <div className="grid sm:grid-cols-2 gap-4">
              {[
                "Operational issues across orders, refunds, and fulfillment",
                "Margin and profitability changes over time",
                "Product-level profitability issues",
                "Inventory levels that impact cash flow"
              ].map((item, i) => (
                <ScrollReveal key={i} delay={i * 100}>
                  <div className="flex items-center space-x-3 p-3 rounded-xl bg-slate-900 border border-slate-800/50">
                    <div className="h-1.5 w-1.5 rounded-full bg-purple-500" />
                    <span className="text-slate-200 font-semibold">{item}</span>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>

          <ScrollReveal delay={400}>
            <div className="mt-32 text-center">
              <p className="text-xl text-slate-400 mt-8 max-w-2xl mx-auto leading-relaxed">
               Most stores lose money because of hidden inefficiencies when they don’t know what’s working, what’s not, and why.<br></br>
              </p>
            </div>
          </ScrollReveal>
          
        </div>
      </section>

      <ReportFeatureShowcase />

      {/* Chat with Your Store */}
      <section className="py-20 bg-slate-900/50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row gap-12 items-start">
              <div className="md:flex-[1.2] w-full order-2 md:order-1">
                <ScrollReveal>
                  <ul className="space-y-4">
                    {[
                      "Ask any question - get an answer instantly",
                      "Always ready, no waiting",
                      "Follow up naturally as new insights emerge",
                      "Answers grounded in your store’s data"
                    ].map((item, i) => (
                      <li key={i} className="flex items-center space-x-3 text-slate-200">
                        <Check className="h-5 w-5 text-purple-500 shrink-0" />
                        <span className="text-xl font-medium">{item}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Small Live Chat Visual */}
                  <div className="mt-12 max-w-lg">
                    <p className="mb-4 text-sm font-semibold uppercase tracking-[0.18em] text-purple-300">
                      Chat with your store to get answers to any unclear issues.
                    </p>
                    <div className="relative group">
                      <div className="absolute inset-0 bg-purple-500/10 blur-3xl rounded-full -z-10 group-hover:bg-purple-500/20 transition-colors" />
                      <AnalysisPlayback />
                    </div>
                  </div>
                </ScrollReveal>
              </div>
              <div className="md:flex-1 order-1 md:order-2">
                <ScrollReveal delay={200}>
                  <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white">Chat with Your Store</h2>
                  <p className="text-xl text-slate-400 leading-relaxed max-w-[88%]">
                    Chat with your store in plain language, follow the thread naturally, and get grounded answers that stay close to what is actually happening in the business. Storita also surfaces smart follow-ups so the next useful question is already in front of you.
                  </p>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Smart Questions Section */}
      <section className="py-24 bg-slate-950">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row gap-12 items-start">
              <div className="md:flex-1">
                <ScrollReveal>
                  <p className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-orange-300">
                    When You Don’t Know What to Ask
                  </p>
                  <p className="mb-4 max-w-lg text-lg leading-relaxed text-slate-300">
                    Storita uses AI to surface the questions your business needs answered.
                  </p>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Smart Questions</h2>
                  <p className="text-xl text-slate-400 mb-8 leading-relaxed max-w-md">
                    Smart Questions keep the investigation moving by bringing forward useful prompts based on your store context, performance patterns, and operational signals.
                  </p>
                  <ul className="space-y-4">
                    {[
                      "Helps discover insights you didn’t know to look for",
                      "Uncovers opportunities for safer, balanced growth",
                      "Identifies seasonal trends before they happen",
                      "Surfaces operational bottlenecks automatically"
                    ].map((item, i) => (
                      <li key={i} className="flex items-center space-x-3 text-slate-200">
                        <Check className="h-5 w-5 text-purple-500 shrink-0" />
                        <span className="font-medium">{item}</span>
                      </li>
                    ))}
                  </ul>
                </ScrollReveal>
              </div>
              <div className="md:flex-[1.5] w-full">
                <ScrollReveal delay={200}>
                  <div className="relative rounded-3xl overflow-hidden border border-slate-800 shadow-2xl bg-slate-900/50">
                    <Image 
                      src="/website/images/features/feature_ai_generated_questions.webp" 
                      alt="Smart Questions" 
                      width={971} 
                      height={651}
                      className="w-full h-auto"
                    />
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Instant Dashboards Section */}
      <section className="py-24 bg-slate-900/50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <ScrollReveal>
              <div className="relative rounded-3xl bg-slate-950 border border-slate-800 overflow-hidden shadow-2xl">
                <Image 
                  src="/website/images/features/feature_instant_dashboards.webp" 
                  alt="Instant Dashboards" 
                  width={1863}
                  height={993}
                  className="w-full h-auto"
                />
              </div>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <div className="max-w-md">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Instant Dashboards</h2>
                <p className="text-xl text-slate-400 mb-8 leading-relaxed">
                  Build your own real-time dashboard instantly. Add any chart from any answer with one click.
                </p>
                <ul className="space-y-4">
                  {[
                    "Track only the metrics that matter to you",
                    "Dashboards evolve as your business evolves",
                    "Share charts with your team instantly",
                    "Real-time data sync across all views"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center space-x-3 text-slate-200">
                      <Check className="h-5 w-5 text-purple-500 shrink-0" />
                      <span className="font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Built Around Store Efficiency (Framing) */}
      <section className="py-24 bg-slate-950">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row gap-12 items-center">
              <div className="md:flex-1">
                <ScrollReveal>
                  <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white text-left">Built Around Store Efficiency</h2>
                  <p className="text-xl text-slate-400 leading-relaxed max-w-lg">
                    Storita is designed around store efficiency as a first-class concept.
                    It looks at how products, inventory, pricing, and operations interact so growth stays balanced as the business scales.
                  </p>
                </ScrollReveal>
              </div>
              <div className="md:flex-1">
                <ScrollReveal delay={200}>
                  <div className="relative p-6">
                    <div className="flex flex-col items-start md:items-center text-left md:text-center">
                      <h3 className="text-2xl font-bold mb-4 text-white">Store&apos;s Performance</h3>
                      <p className="text-slate-400 mb-8 max-w-sm">
                        Get an instant diagnostic of your store's operational performance and find the leaks costing you profit.
                      </p>
                      <WebsiteCtaLink href="/login">Start Now</WebsiteCtaLink>
                    </div>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes Section */}
      <section className="py-24 bg-slate-900/30">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <ScrollReveal>
              <h2 className="text-3xl font-bold mb-8 text-center text-white">
                Outcomes, Not Just Reports
              </h2>
            </ScrollReveal>

            <ScrollReveal delay={100}>
              <p className="text-xl text-slate-400 text-center mb-12 max-w-2xl mx-auto">
                Storita turns store signals into soft, data-driven recommendations your team can review and act on with context.
              </p>
            </ScrollReveal>

            <div className="space-y-4 max-w-3xl mx-auto">
              {[
                "You may want to review SKUs with rising refunds.",
                "It could be helpful to add more products to this top-performing category.",
                "Encouraging lower-cost payment methods could improve margins."
              ].map((text, i) => (
                <ScrollReveal key={i} delay={i * 100 + 200}>
                  <div className="p-4 rounded-xl bg-slate-900 border border-slate-800 text-slate-200 font-medium">
                    “{text}”
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Continuous Analysis Section */}
      <section className="py-24 bg-slate-950">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <ScrollReveal>
              <h2 className="text-3xl font-bold mb-12 text-center text-white">
                What Storita Continuously Analyzes
              </h2>
            </ScrollReveal>

            <div className="grid md:grid-cols-2 gap-12 items-start">
              <ScrollReveal delay={200}>
                <div className="relative rounded-3xl overflow-hidden border border-slate-800 shadow-2xl bg-slate-900/50 p-2 md:p-3">
                  <Image 
                    src="/website/images/features/feature_continuously_analyze.webp" 
                    alt="What Storita Continuously Analyzes" 
                    width={701} 
                    height={417}
                    className="w-full h-auto rounded-2xl"
                  />
                </div>
              </ScrollReveal>

              <div className="grid gap-4">
                {[
                  "Product-level efficiency and margins",
                  "Category performance and balance",
                  "Inventory allocation and cash drain",
                  "Refund and return impact",
                  "Payment method cost vs usage",
                  "Sales stability and consistency over time",
                  "Performance across seasons"
                ].map((item, i) => (
                  <ScrollReveal key={i} delay={i * 100 + 400}>
                    <div className="p-4 rounded-xl bg-slate-900 border border-slate-800 text-slate-200 font-medium">
                      {item}
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Practical Meaning (Section B) */}
      <section className="py-24 bg-slate-900/50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <ScrollReveal delay={100}>
              <div className="p-8 rounded-3xl bg-slate-950 border border-slate-800 h-full">
                <h3 className="text-2xl font-bold mb-4 text-white">Efficiency = Profitability</h3>
                <p className="text-slate-400 mb-6 max-w-sm">
                  Storita helps you see the weighting balance of your business:
                </p>
                <ul className="space-y-3">
                  {[
                    "Which products waste time and money",
                    "Where inventory is misallocated",
                    "Which refunds silently kill profit"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start space-x-3 text-slate-300 text-sm">
                      <div className="h-1.5 w-1.5 rounded-full bg-purple-500 mt-1.5 shrink-0" />
                      <span className="font-medium text-slate-200">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className="p-8 rounded-3xl bg-slate-950 border border-slate-800 h-full">
                <h3 className="text-2xl font-bold mb-4 text-white">Continuous Measurement</h3>
                <p className="text-slate-400 mb-6 max-w-sm">
                  Storita helps you see efficiency continuously across every season:
                </p>
                <ul className="space-y-3">
                  {[
                    "Works in winter and summer",
                    "High season and slow months",
                    "Tracks how decisions impact profit over time"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start space-x-3 text-slate-300 text-sm">
                      <div className="h-1.5 w-1.5 rounded-full bg-purple-500 mt-1.5 shrink-0" />
                      <span className="font-medium text-slate-200">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <DataSourcesSection />

      {/* Testimonial Section - Soltam */}
      <section className="py-24 bg-slate-950 border-t border-slate-900/50 relative overflow-hidden">
        <div className="absolute inset-0 bg-purple-900/5 pointer-events-none" />
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <ScrollReveal>
            <div className="max-w-4xl mx-auto">
              <div className="bg-slate-900/40 backdrop-blur-sm border border-slate-800/60 rounded-3xl p-8 md:p-12 relative">
                {/* Decorative Quote Icon */}
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-slate-950 border border-slate-800 rounded-full p-4 text-purple-400 shadow-xl">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor" className="opacity-80">
                    <path d="M14.017 21L14.017 18C14.017 16.8954 13.1216 16 12.017 16H9C9.55228 16 10 15.5523 10 15V9C10 8.44772 9.55228 8 9 8H5C4.44772 8 4 8.44772 4 9V15C4 18.3137 6.68629 21 10 21H14.017ZM21.017 21L21.017 18C21.017 16.8954 20.1216 16 19.017 16H16C16.5523 16 17 15.5523 17 15V9C17 8.44772 16.5523 8 16 8H12C11.4477 8 11 8.44772 11 9V15C11 18.3137 13.6863 21 17 21H21.017Z" />
                  </svg>
                </div>

                <div className="text-center pt-6">
                  <p className="text-xl md:text-3xl font-medium text-slate-200 leading-relaxed mb-8">
                    "Identifying non-selling products required digging through reports, so it rarely happened. Now we see them immediately."
                  </p>
                  
                   <div className="flex flex-col items-center gap-2">
                    <span className="text-slate-400 font-medium tracking-wide text-sm uppercase">Trusted by</span>
                    <a 
                      href="https://soltam.co.il/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg md:text-xl font-bold text-white hover:text-purple-300 transition-colors flex items-center gap-2 group"
                    >
                       Sapir Borchov, Soltam
                       <ChevronRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-purple-400" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-slate-950 border-t border-slate-900">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
            Ready to see what your data is hiding?
          </h2>
          <WebsiteCtaLink href="/login">Start Now</WebsiteCtaLink>
        </div>
      </section>
    </div>
  );
}
